// TREASURY v2 — ONE REGISTER (UI rebuild batch 2; Astra's anatomy under the operator's 26 Oct ruling, "closer to that
// version").
//
// Astra's columns: Account (the name over "kind · asset") · Book balance · Outside the books · the chevron (a read state
// only when it is not a good read — "Old read", "No rate" — under the figure; Astra, 1 Nov 2026).
// Rows are grouped by rail as the response's `groups` give them (Astra's content: "group accounts by rail"), each group
// under one plain label with its subtotal as the label's figure (restored 1 Nov 2026, his "restore most the lost
// things … with the improved UI"). One row per account·asset ("Office cash" / "Cash · AED",
// "Bitget" / "Exchange · BTC"): that is the row an outside reading belongs to — Bitget's BTC is 0 in the books and
// 3.1842 on the venue on ONE line. Treasury crypto stays one row per wallet with its USDT roll-up (the operator's
// 4 Sept ruling) and opens to its coins. The outside reading is never added to a book figure; a row without one reads
// "—" (no reading given, not a zero). A group's quiet rows, and the groups that start folded today (acquired, other),
// sit behind "N more" (the ruling: top N, then "N more"). The chevron opens today's per-account expansion (N-036): a
// wallet's coins, a currency's accounts, and the account's Money movements.
import { useContext, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { TreasuryGroup, TreasuryValuation } from '../../../lib/api';
import { displayAsset, fmtAmount, fmtQuantity } from '../../../lib/format';
import { foldAsset, railKindName, railName, valuationBasisName } from '../../../lib/naming';
import {
  COLLAPSED_BY_DEFAULT, approx, drillSources, fmtEstimate, fmtStampedValuation, headline, isOutsideOnly, isOwed, readsAsWallets,
  rollUp, splitLines, splitRails, stampedValuation, waitingAmount, waitingCoins, whereIs, type GroupLine, type RailRow,
  type AssetRate, type Headline, type Waiting,
} from './logic';
import { AccountName, FOCUS_ROW, FocusBar, FocusRail, GroupHeadline, RailHistory, RollUpFigure, ValuationNote, useFocusRow } from './figures';

/** The register's columns, written out whole so Tailwind keeps them. */
const COLS = 'lg:grid-cols-[minmax(0,1fr)_minmax(0,15rem)_minmax(0,13rem)_2.5rem]';

/** Astra's column heads (laptop). */
export function RegisterHead() {
  return (
    <div className={`hidden border-b border-stroke px-5 py-3 text-label font-medium uppercase tracking-wider text-tg-hint lg:grid lg:gap-x-4 ${COLS}`}>
      <span>Account</span>
      <span className="text-right">Book balance</span>
      <span className="text-right">Outside the books</span>
      <span aria-hidden />
    </div>
  );
}

/** Is the read behind these rows the current one? A failed refresh keeps its rows on screen, marked — never "Known". */
type Fresh = 'known' | 'old';

/** A row's read state, said ONLY when it is not a good read (Astra, 1 Nov 2026: twelve "Known" badges said nothing): rows
 *  from a refresh that failed say "Old read", a figure the books could not price "No rate"; a good read says nothing. */
function StatePill({ fresh, unpriced }: { fresh: Fresh; unpriced?: boolean }) {
  if (fresh !== 'old' && !unpriced) return null;
  return <span className="inline-block rounded bg-warn-bg px-2 py-0.5 text-meta text-warn">{fresh === 'old' ? 'Old read' : 'No rate'}</span>;
}

/** The kind of an account in a word, from its slug ("exchange.bitget" → "Exchange"). */
function kindOf(slug: string | null | undefined): string {
  return railKindName(slug?.split('.')[0]);
}

/** What the chain or the venue shows, apart from the book figure: the column on the laptop ("—" when none was given),
 *  a line led by "Outside" on the phone (nothing when none). */
function OutsideCell({ items }: { items: readonly Waiting[] }) {
  if (items.length === 0) return <span className="hidden text-tg-hint lg:inline" title="No outside reading">—</span>;
  return (
    <>
      {items.map((w) => (
        <span key={w.asset} className="block text-meta text-tg-hint lg:text-body">
          <span className="text-warn lg:hidden">Outside · </span>
          <span className="font-mono tabular text-tg-text">{waitingAmount(w)}</span>{' '}
          <span className="lg:block lg:text-meta">{w.outside.words}</span>
        </span>
      ))}
    </>
  );
}

/** ONE ROW, Astra's: the whole row is the toggle, its chevron at the end. On the phone a card — name and chevron, the
 *  "kind · asset" line, the figure with its pill, the outside line. */
function Row({ name, sub, book, outside, pill, open, onToggle, focused = false, rowRef, children }: {
  name: ReactNode;
  sub?: ReactNode;
  book: ReactNode;
  outside: readonly Waiting[];
  pill: ReactNode;
  open: boolean;
  onToggle: () => void;
  focused?: boolean;
  rowRef?: React.Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  return (
    <div ref={rowRef} className={`relative border-b border-stroke-soft last:border-b-0 ${focused ? FOCUS_ROW : ''}`}>
      {focused && <FocusBar />}
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 px-4 py-4 text-left transition-colors hover:bg-panel-2 lg:items-center lg:gap-x-4 lg:px-5 ${COLS}`}
      >
        <span className="col-start-1 row-start-1 min-w-0">
          <span className="block truncate text-body font-medium lg:whitespace-normal">{name}</span>
          {sub && <span className="mt-1 block truncate text-meta text-tg-hint">{sub}</span>}
        </span>
        <span className="col-start-1 row-start-2 mt-3 min-w-0 text-title lg:col-start-2 lg:row-start-1 lg:mt-0 lg:text-right lg:text-body">
          {book}
          {pill && <span className="mt-1 hidden lg:block">{pill}</span>}
        </span>
        {pill && <span className="col-start-2 row-start-2 mt-3 self-start justify-self-end lg:hidden">{pill}</span>}
        <span className={`col-span-2 col-start-1 row-start-3 min-w-0 lg:col-span-1 lg:col-start-3 lg:row-start-1 lg:text-right ${outside.length > 0 ? 'mt-1 lg:mt-0' : ''}`}>
          <OutsideCell items={outside} />
        </span>
        <span className="col-start-2 row-start-1 grid h-6 w-6 place-items-center justify-self-end text-tg-hint lg:col-start-4" aria-hidden>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>
      {open && <div className="border-t border-stroke-soft bg-panel-2 px-4 pb-3 pt-1 lg:px-5">{children}</div>}
    </div>
  );
}

/** A line inside an expansion: a coin of a wallet, an account behind a currency. */
function SubLine({ name, book, outside, slug, focused = false }: {
  name: ReactNode; book: ReactNode; outside: readonly Waiting[]; slug?: string | null; focused?: boolean;
}) {
  return (
    <div className={`relative grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 border-b border-stroke-soft py-1 last:border-b-0 lg:gap-x-4 ${COLS} ${focused ? FOCUS_ROW : ''}`}>
      {focused && <FocusBar />}
      <span className="min-w-0 break-words pl-3 text-meta text-tg-hint">{name}</span>
      <span className="text-right text-body">{book}</span>
      <span className="col-span-3 row-start-2 pl-3 lg:col-span-1 lg:col-start-3 lg:row-start-1 lg:pl-0 lg:text-right">
        <OutsideCell items={outside} />
      </span>
      <span className="justify-self-end lg:col-start-4 lg:row-start-1">{slug ? <RailHistory slug={slug} visible /> : null}</span>
    </div>
  );
}

/** The way into an account's history, at the foot of its expansion. */
function MovementsLink({ slug }: { slug?: string | null }) {
  if (!slug) return null;
  return (
    <Link to={`/movements?rail=${encodeURIComponent(slug)}`} className="btn-link mt-2 inline-flex min-h-[44px] items-center text-body lg:min-h-[40px]">
      Money movements →
    </Link>
  );
}

/** ONE WALLET, ONE ROW (the 4 Sept roll-up): what the wallet is worth; its coins and their exact quantities inside. */
function WalletRow({ rail, valuation, fresh }: { rail: RailRow; valuation: TreasuryValuation; fresh: Fresh }) {
  const { focused, ref } = useFocusRow(rail.slug);
  const [open, setOpen] = useState(focused);
  const r = rollUp(rail, valuation);
  const waiting = waitingCoins(rail);
  const allWaiting = waiting.length > 0 && waiting.length === rail.assets.length;
  const coins = rail.assets.map((a) => displayAsset(a.asset));
  const sub = [kindOf(rail.slug), coins.length > 2 ? `${coins.slice(0, 2).join(' · ')} +${coins.length - 2}` : coins.join(' · ')].filter(Boolean).join(' · ');
  return (
    <Row rowRef={ref} focused={focused} open={open} onToggle={() => setOpen(!open)} name={railName(rail)} sub={sub}
      book={<><RollUpFigure r={r} />{!allWaiting && <ValuationNote r={r} />}</>} outside={waiting}
      pill={<StatePill fresh={fresh} unpriced={r.kind === 'unavailable' || r.kind === 'unpriceable'} />}>
      {rail.assets.map((a) => {
        const out = isOutsideOnly(a);
        const caption = out || !valuation.available
          ? null
          : a.value_usdt === null
            ? <span className="text-warn">no price</span>
            : foldAsset(a.asset) === foldAsset(valuation.quote)
              ? <>counted</>
              : <>{approx(fmtEstimate(a.value_usdt, valuation.quote))} {valuation.quote} · {valuationBasisName(a.basis)}</>;
        return (
          <SubLine key={a.asset} name={displayAsset(a.asset)}
            book={<><span className={`whitespace-nowrap font-mono tabular ${a.balance < 0 ? 'text-neg' : ''}`}>{fmtQuantity(a.balance, a.asset)}</span>{' '}<span className="text-meta text-tg-hint">{displayAsset(a.asset)}</span>{caption && <span className="block text-meta text-tg-hint">{caption}</span>}</>}
            outside={out ? [{ asset: a.asset, outside: a.outside! }] : []} />
        );
      })}
      <MovementsLink slug={rail.slug} />
    </Row>
  );
}

/** A per-asset line (office cash): one account → that account's name over "Cash · AED"; several → the currency, and
 *  the accounts inside. */
function AssetRow({ line, drill, desk, fresh }: { line: GroupLine; drill: boolean; desk: string; fresh: Fresh }) {
  const want = useContext(FocusRail);
  const sources = drillSources(line);
  const one = sources.length === 1 ? sources[0] : undefined;
  const { focused, ref } = useFocusRow(one?.slug);
  const mine = !!want && sources.some((s) => s.slug === want);
  const [open, setOpen] = useState(mine && !one);
  const val = stampedValuation(line);
  const asset = displayAsset(line.asset);
  const waiting = one && isOutsideOnly(one) ? [{ asset: line.asset ?? '', outside: one.outside! }] : [];
  return (
    <Row
      rowRef={ref}
      focused={focused}
      open={open}
      onToggle={() => setOpen(!open)}
      name={one ? <AccountName row={one} desk={desk} /> : asset}
      sub={one ? [kindOf(one.slug), asset].filter(Boolean).join(' · ') : `${asset} · ${sources.length} ${drill ? 'wallets' : 'accounts'}`}
      book={(
        <>
          <span className={`font-mono tabular font-semibold ${(line.balance ?? 0) < 0 ? 'text-neg' : ''}`}>{fmtAmount(line.balance, line.asset)}</span>{' '}
          <span className="text-meta text-tg-hint">{asset}</span>
          {val && <span className="block text-meta text-tg-hint">{fmtStampedValuation(val)}</span>}
        </>
      )}
      outside={waiting}
      pill={<StatePill fresh={fresh} />}
    >
      {one
        ? <MovementsLink slug={one.slug} />
        : sources.map((s, i) => (
          <SubLine key={`${s.account}:${i}`} name={<AccountName row={s} desk={desk} />} slug={s.slug} focused={s.slug === want}
            book={<><span className={`whitespace-nowrap font-mono tabular ${s.balance < 0 ? 'text-neg' : ''}`}>{fmtAmount(s.balance, line.asset)}</span>{' '}<span className="text-meta text-tg-hint">{asset}</span></>}
            outside={isOutsideOnly(s) ? [{ asset: line.asset ?? '', outside: s.outside! }] : []} />
        ))}
    </Row>
  );
}

/** One account·asset of a bank, an exchange or a running account. A running account below zero is owed, and says so
 *  under its signed figure. */
function AccountAssetRow({ line, b, first, desk, fresh }: {
  line: GroupLine;
  b: NonNullable<GroupLine['balances']>[number];
  first: boolean;
  desk: string;
  fresh: Fresh;
}) {
  const { focused, ref } = useFocusRow(first ? line.slug : null);
  const want = useContext(FocusRail);
  const [open, setOpen] = useState(false);
  const owed = isOwed(line, b.balance);
  return (
    <Row
      rowRef={ref}
      focused={focused || (!!want && line.slug === want)}
      open={open}
      onToggle={() => setOpen(!open)}
      name={<AccountName row={line} desk={desk} />}
      sub={[kindOf(line.slug), displayAsset(b.asset)].filter(Boolean).join(' · ')}
      book={(
        <>
          {/* Owed to a running account: the amount, its unit, then the words — never a bare signed figure. */}
          <span className={`whitespace-nowrap font-mono tabular font-semibold ${owed || b.balance >= 0 ? '' : 'text-neg'}`}>{fmtAmount(owed ? Math.abs(b.balance) : b.balance, b.asset)}</span>{' '}
          <span className="text-meta text-tg-hint">{displayAsset(b.asset)}</span>
          {/* "owed to the coastal" read the slug, not words (the audit, 1 Nov 2026): one word, as the totals say it. */}
          {owed && <span className="block text-meta text-tg-hint">Owed</span>}
        </>
      )}
      outside={isOutsideOnly(b) ? [{ asset: b.asset, outside: b.outside! }] : []}
      pill={<StatePill fresh={fresh} />}
    >
      <MovementsLink slug={line.slug} />
    </Row>
  );
}

/** A GROUP'S SUBTOTAL, only where it adds something (Astra, 1 Nov 2026): a currency held on several accounts of the group, or
 *  the group's coins as one priced estimate. A currency on one account repeats its own row, and is left out. */
function groupSubtotal(group: TreasuryGroup, rates: Map<string, AssetRate>, valuation?: TreasuryValuation): Headline {
  const accounts = new Map<string, number>();
  for (const l of group.lines) {
    if (group.style === 'asset_lines') { if (l.asset) accounts.set(l.asset, (accounts.get(l.asset) ?? 0) + Math.max(1, drillSources(l).length)); }
    else for (const b of l.balances ?? []) accounts.set(b.asset, (accounts.get(b.asset) ?? 0) + 1);
  }
  const h = headline(group.subtotals ?? {}, rates, valuation);
  return { ...h, fiat: h.fiat.filter(([a]) => (accounts.get(a) ?? 0) > 1 || [...accounts.keys()].filter((k) => displayAsset(k) === a).reduce((n, k) => n + (accounts.get(k) ?? 0), 0) > 1) };
}

/** How many rows a group draws — the register's foot counts them. */
export function rowCount(group: TreasuryGroup, valuation?: TreasuryValuation): number {
  if (readsAsWallets(group, valuation)) return splitRails(group).rails.length;
  const { lines } = splitLines(group);
  return group.style === 'asset_lines' ? lines.length : lines.reduce((n, l) => n + (l.balances?.length ?? 0), 0);
}

/** ONE GROUP OF THE REGISTER: a plain label (the rail), its rows, and "N more" for its quiet rows. */
export function GroupRows({ group, valuation, desk, fresh, rates, all = false }: {
  group: TreasuryGroup;
  valuation?: TreasuryValuation;
  desk: string;
  fresh: Fresh;
  /** The desk's rates, for the group's coins as one estimate in its subtotal. */
  rates?: Map<string, AssetRate>;
  /** "Show all" on the register's foot: every group's quiet rows drawn. */
  all?: boolean;
}) {
  const want = useContext(FocusRail);
  const at = want ? whereIs(group, valuation, want) : null;
  // A group that starts folded today (acquired, other) shows all its rows behind "N more"; a link into it opens it.
  const folded = COLLAPSED_BY_DEFAULT.has(group.id);
  const [moreOwn, setMore] = useState(at === 'quiet' || (folded && at !== null));
  const more = all || moreOwn;
  const wallets = readsAsWallets(group, valuation);
  const { shown, quiet } = splitLines(group);
  const rails = splitRails(group);

  const draw = (items: { lines: GroupLine[]; rails: RailRow[] }) => wallets
    ? items.rails.map((rv) => <WalletRow key={rv.slug} rail={rv} valuation={valuation!} fresh={fresh} />)
    : group.style === 'asset_lines'
      ? items.lines.map((l) => <AssetRow key={l.key} line={l} drill={group.id === 'treasury_crypto'} desk={desk} fresh={fresh} />)
      : items.lines.flatMap((l) => (l.balances ?? []).map((b, i) => (
        <AccountAssetRow key={`${l.key}:${b.account}:${b.asset}`} line={l} b={b} first={i === 0} desk={desk} fresh={fresh} />
      )));
  const lead = folded ? { lines: [], rails: [] } : { lines: shown, rails: rails.shown };
  const rest = folded ? { lines: [...shown, ...quiet], rails: [...rails.shown, ...rails.quiet] } : { lines: quiet, rails: rails.quiet };
  const restCount = wallets ? rest.rails.length
    : group.style === 'asset_lines' ? rest.lines.length : rest.lines.reduce((n, l) => n + (l.balances?.length ?? 0), 0);

  return (
    <section aria-label={group.label}>
      {/* The group's subtotal is the label's figure, quiet, at its right — never an extra row. */}
      <h3 className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-stroke-soft bg-panel-2 px-4 py-2 text-label font-medium uppercase tracking-wider text-tg-hint lg:px-5">
        <span>{group.label}</span>
        {rates && <GroupHeadline h={groupSubtotal(group, rates, valuation)} className="text-meta font-normal normal-case tracking-normal" />}
      </h3>
      {draw(lead)}
      {more && draw(rest)}
      {restCount > 0 && !all && (
        <button type="button" aria-expanded={more} onClick={() => setMore(!more)}
          className="flex min-h-[44px] w-full items-center border-b border-stroke-soft px-4 text-left text-body text-tg-link last:border-b-0 hover:underline lg:min-h-[40px] lg:px-5">
          {more ? 'Show fewer' : `${restCount} more →`}
        </button>
      )}
    </section>
  );
}
